import { useAuth } from '@/loaders/Auth'

export const API_BASE_URL = import.meta.env.VITE_API_URL

export interface FetchOptions extends RequestInit {
	method?: string
	body?: any
}

const isBinaryBody = (body: any): boolean => {
	return (
		body instanceof ArrayBuffer ||
		ArrayBuffer.isView(body) ||
		body instanceof Blob ||
		body instanceof File ||
		body instanceof FormData
	)
}

const toRecord = (h?: HeadersInit): Record<string, string> => {
	if (!h) return {}
	if (h instanceof Headers) return Object.fromEntries(h.entries())
	if (Array.isArray(h)) return Object.fromEntries(h)
	return h as Record<string, string>
}

export const useHoneyGridApiFetch = () => {
	const { getToken, logout } = useAuth()

	return async (endpoint: string, options: FetchOptions = {}) => {
		const token = await getToken()
		const { body, ...rest } = options

		if (!token) {
			await logout()
			throw { status: 401 }
		}

		// Prepare headers with override support
		const headers: Record<string, string> = {
			Authorization: `Bearer ${token}`,
			...toRecord(options.headers),
		}

		// If no content-type provided and body is JSON-able, set default
		if (!isBinaryBody(body)) {
			if (!headers['Content-Type']) {
				headers['Content-Type'] = 'application/json'
			}
		} else {
			// Remove json content-type if user mistakenly added it for binary payloads
			if (headers['Content-Type'] === 'application/json') {
				delete headers['Content-Type']
			}
		}

		const finalBody =
			body && !isBinaryBody(body) && headers['Content-Type'] === 'application/json'
				? JSON.stringify(body)
				: body

		// Allow fully-qualified URLs (e.g., https://...)
		const url = endpoint.startsWith('http') ? endpoint : `${API_BASE_URL}/jwt/${endpoint}`

		const response = await fetch(url, {
			...rest,
			body: finalBody,
			headers,
		})

		// If unauthorized, redirect to login and throw a typed error for react-query
		if (response.status === 401) {
			await logout()
			throw { status: 401 }
		}

		// If response has no content, just return void
		if (response.status === 204) return

		const data = await response.json().catch(() => undefined)

		if (!response.ok) {
			throw { status: response.status, data }
		}

		return data
	}
}
